import React from "react";
import Button from "components/button/Button";
import InputField from "components/fields/InputField";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@chakra-ui/modal";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { addBranchAdmin, getBranch, updateBranchAdmin } from "api/BranchAPI";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Actions } from "constants/actions";

interface IFormAdminModalProps {
  title: React.ReactNode;
  body?: React.ReactNode;
  action: Actions;
  data?: any;
  refreshData?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  onMove?: () => void;
}

const FormAdminModal: React.FC<IFormAdminModalProps> = (props) => {
  const { slug } = useParams<{ slug: string }>();
  const { title, body, action, data, refreshData, onCancel, onDelete, onMove } =
    props;
  const [branchID, setBranchID] = React.useState(0);
  const [userID, setUserID] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const {
    register,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    setIsLoading(true);

    delete data.confirm_password;
    let promise = null;

    if (action === Actions.CREATE) {
      const payload = {
        ...data,
        name: data?.name,
        email: data?.email,
        password: data?.password,
        store_id: branchID,
      };

      promise = addBranchAdmin(payload);
    } else if (action === Actions.UPDATE) {
      const payload = {
        name: data.name,
        email: data.email,
      };
      promise = updateBranchAdmin(payload, userID, slug);
    }

    promise
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          toast.success(
            `Berhasil ${
              action === Actions.CREATE ? "menambahkan" : "mengubah"
            } admin!`
          );
          refreshData();
          onCancel();
        }
        setIsLoading(false);
      })
      .catch((err) => {
        toast.error(
          err?.response.data.message || "Gagal menambahkan admin, coba lagi!"
        );
        setIsLoading(false);
      });
  };

  const getBranchID = () => {
    const promise = getBranch(slug);
    promise
      .then((res) => {
        setBranchID(res?.data?.data?.id);
      })
      .catch((err) => {
        getBranchID();
        toast.error(
          err?.response.data.message ||
            "Gagal mendapatkan ID Toko, mendapatkan ulang..."
        );
      });
  };

  React.useEffect(() => {
    if (slug) getBranchID();
    if (data) {
      setUserID(data.id);
      reset(data);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {(action === Actions.CREATE || action === Actions.UPDATE) && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InputField
              variant="auth"
              extra="mb-3"
              label="Nama"
              placeholder="Masukkan nama"
              id="name"
              type="text"
              register={register}
              errors={errors}
              validationSchema={{
                required: "Nama harus diisi!",
              }}
            />
            <InputField
              variant="auth"
              extra="mb-3"
              label="Email"
              placeholder="mail@example.com"
              id="email"
              type="email"
              register={register}
              errors={errors}
              validationSchema={{
                required: "Email harus diisi!",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "Email tidak valid!",
                },
              }}
            />

            {action === Actions.CREATE && (
              <>
                {/* Password */}
                <InputField
                  variant="auth"
                  extra="mb-3"
                  label="Password"
                  placeholder="Min. 8 karakter"
                  id="password"
                  register={register}
                  errors={errors}
                  validationSchema={{
                    required: "Password harus diisi!",
                    minLength: {
                      value: 8,
                      message: "Password minimal 8 karakter!",
                    },
                  }}
                  type="password"
                />
                <InputField
                  variant="auth"
                  extra="mb-3"
                  label="Konfirmasi Password"
                  placeholder="Masukkan kembali password"
                  id="confirm_password"
                  register={register}
                  errors={errors}
                  validationSchema={{
                    required: "Konfirmasi Password harus diisi!",
                    validate: (value) =>
                      value === getValues("password") ||
                      "Konfirmasi Password tidak sesuai!",
                  }}
                  type="password"
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {" "}
            <div className="flex gap-3">
              <Button
                size="fit"
                children={
                  <span className="text-white">
                    {action === Actions.CREATE ? "Tambah" : "Simpan"}
                  </span>
                }
                variant="primary"
                isLoading={isLoading}
                type="submit"
              />
            </div>
          </ModalFooter>
        </form>
      )}
      {action === Actions.DELETE && (
        <div>
          <ModalHeader>Hapus Admin</ModalHeader>
          <ModalCloseButton />
          <ModalBody>{title}</ModalBody>
          <ModalFooter>
            {" "}
            <div className="flex gap-3">
              <Button
                size="fit"
                children="Batal"
                variant="gray"
                onClick={onCancel}
              />
              <Button
                size="fit"
                children={<span className="text-white">Ya, hapus</span>}
                variant="danger"
                onClick={() => {
                  onDelete();
                  onCancel();
                }}
              />
            </div>
          </ModalFooter>
        </div>
      )}
      {action === Actions.MOVE && (
        <div>
          <ModalHeader>Pindah Admin ke Toko Cabang Lain</ModalHeader>
          <ModalCloseButton />
          <ModalBody>{body}</ModalBody>
          <ModalFooter>
            {" "}
            <div className="flex gap-3">
              <Button
                size="fit"
                children="Batal"
                variant="gray"
                onClick={onCancel}
              />
              <Button
                size="fit"
                children={<span className="text-white">Pindah Admin</span>}
                variant="primary"
                onClick={() => {
                  onMove();
                  onCancel();
                }}
              />
            </div>
          </ModalFooter>
        </div>
      )}
    </>
  );
};

export default FormAdminModal;
